import { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { totalTimeMin, difficultyLabel } from "../data/recipes";
import {
  pickDurationMin,
  getOvernightDurations,
  OVERNIGHT_DAY2_START_HOUR,
} from "../lib/calendarUtils";
import {
  color, font, space, radius, shadow,
  stack, divider,
} from "../lib/mcmTokens";
import MealPrepTimeline from "./MealPrepTimeline";
import StepTimeline from "./StepTimeline";
import { ServingsStepper } from "./ServingsStepper";

const SHORT_DAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

// Bottom-sheet drawer for a scheduled meal. Restyled to the MCM
// vocabulary — cream parchment surface, Fraunces italic titles, Forma
// DJR Deck body, gold/burnt accents only where they earn it.
//
// Adds inline date + time editing for the meal creator: tap the date
// chip to swap days, the time chip to nudge the clock — both fire
// onChangeScheduledFor(iso) which Plan wires to updateMeal.
//
// Renders TWO shapes:
//   1. Locked recipe — recipe couldn't be resolved.
//   2. Full recipe — every section.
//
// Portal to document.body — a `position: fixed` descendant inside a
// transformed ancestor (Plan's tab container) would otherwise resolve
// inset:0 against the calendar's scroll height, not the viewport.
export default function MealDetailDrawer({
  meal, recipe, userId, nameFor,
  family = [], prepRows = [],
  onDismissPrep, onUndismissPrep,
  onCookNow, onClaim, onUnclaim, onChangeCook, onChangeServings,
  onChangeScheduledFor, onChangeEatAnchor,
  onDelete, onClose,
  // Weekly-planner concept meals (recipe_snapshot._concept, steps
  // empty): COOK NOW is replaced by GENERATE FULL RECIPE — the paid,
  // explicit expansion step. `generating` disables it while the
  // stream runs.
  onGenerateRecipe, generating = false,
  // Failed background stub → RETRY (re-enters backgroundRecipeGen
  // with the localStorage-stashed inputs).
  onRetryGenerate,
}) {
  const [confirming, setConfirming] = useState(false);
  const isConcept = Boolean(recipe?._concept);
  // Background-generation stub states (backgroundRecipeGen writes
  // _gen onto the user_recipes row; realtime flips this drawer live
  // when the finished recipe lands).
  const isGenPending = recipe?._gen === "pending";
  const isGenFailed = recipe?._gen === "failed";

  if (!recipe) {
    const viewerIsAuthor = meal.user_id === userId;
    const authorName     = nameFor ? nameFor(meal.user_id) : null;
    const prettyTitle    = (meal.recipe_slug || "")
      .replace(/[-_]+/g, " ")
      .replace(/\b\w/g, c => c.toUpperCase())
      .trim() || "Untitled recipe";
    return createPortal(
      <div style={overlayStyle} onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
        <div style={panelStyle}>
          <div style={dragHandleStyle} />
          <div style={headerSectionStyle}>
            <div style={kickerStyle}>
              {meal.meal_slot ? `${meal.meal_slot.toUpperCase()} · ` : ""}{fmtClock(meal.scheduled_for).toUpperCase()} · UNAVAILABLE
            </div>
            <div style={titleStyle}>{prettyTitle}</div>
            {authorName && !viewerIsAuthor && (
              <div style={metaStyle}>Scheduled by {authorName}</div>
            )}
          </div>
          <div style={bodyScrollStyle}>
            <div style={infoCardStyle}>
              {viewerIsAuthor
                ? "This recipe isn't in your library. It may have been deleted, or the save didn't land — schedule it again from your recipes."
                : <>{authorName || "The author"} hasn't shared this recipe yet. Ask them to re-open it and tap <b>SHARE WITH FAMILY</b>.</>
              }
            </div>
          </div>
          <div style={footerSectionStyle}>
            {viewerIsAuthor && onDelete ? (
              <button onClick={() => onDelete(meal.id)} style={dangerBtn}>REMOVE FROM CALENDAR</button>
            ) : null}
            <button onClick={onClose} style={ghostQuietBtn}>CLOSE</button>
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  const activeNotifs = Object.values(meal.notification_settings || {}).filter(Boolean).length;
  const totalNotifs  = (recipe.prepNotifications || []).length;
  const creatorName  = nameFor ? nameFor(meal.user_id) : null;
  const isRequest    = meal.cook_id == null;
  const cookName     = !isRequest && nameFor ? nameFor(meal.cook_id) : null;
  const isCreator    = meal.user_id === userId;
  const isCook       = meal.cook_id === userId;
  const showServings = isCreator && onChangeServings;
  const servings     = meal.servings ?? 2;
  const canReschedule = isCreator && onChangeScheduledFor;
  const canChangeAnchor = isCreator && onChangeEatAnchor;

  // Eat-anchored meals: scheduled_for IS the eat moment; the cook
  // start derives at render time. Surface both so the user knows when
  // to start AND when they'll eat.
  //
  // Overnight bakes have TWO distinct windows. Day 1 (evening prep)
  // backdates from Day 2's start; Day 2 (morning bake) anchors
  // backward from eat time so the loaf comes out hot. Single-segment
  // recipes keep the legacy "cookStart = eat - fullDuration" shape.
  const isEatAnchor  = !!meal.eat_anchor;
  const eatTime      = isEatAnchor ? new Date(meal.scheduled_for) : null;
  const overnightDur = getOvernightDurations(recipe);
  const day2StartTime = (() => {
    if (!overnightDur) return null;
    if (isEatAnchor) {
      return new Date(eatTime.getTime() - overnightDur.day2Min * 60_000);
    }
    // Cook-anchored: anchor at the per-meal day2_hour or the default.
    const hour = Number.isFinite(meal.day2_hour) ? Math.floor(meal.day2_hour) : OVERNIGHT_DAY2_START_HOUR;
    const d = new Date(meal.scheduled_for);
    d.setDate(d.getDate() + 1);
    d.setHours(hour, 0, 0, 0);
    return d;
  })();
  const cookStart    = (() => {
    if (!isEatAnchor) return null;
    if (overnightDur) {
      const offsetMin = overnightDur.day1ActiveMin + overnightDur.overnightMin + overnightDur.day2Min;
      return new Date(eatTime.getTime() - offsetMin * 60_000);
    }
    return new Date(eatTime.getTime() - pickDurationMin(recipe, 30) * 60_000);
  })();

  const sched = new Date(meal.scheduled_for);

  // Apply a date or time edit by composing the parts back into a new
  // ISO string. Native `<input type="date">` returns YYYY-MM-DD,
  // `<input type="time">` returns HH:MM — both interpreted in local
  // time so the user's pick lands on the right wall-clock moment.
  const applyDate = (yyyymmdd) => {
    if (!yyyymmdd || !canReschedule) return;
    const [y, mo, d] = yyyymmdd.split("-").map(Number);
    const next = new Date(sched);
    next.setFullYear(y, mo - 1, d);
    onChangeScheduledFor(next.toISOString());
  };
  const applyTime = (hhmm) => {
    if (!hhmm || !canReschedule) return;
    const [h, m] = hhmm.split(":").map(Number);
    const next = new Date(sched);
    next.setHours(h, m, 0, 0);
    onChangeScheduledFor(next.toISOString());
  };

  return createPortal(
    <div style={overlayStyle} onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div style={panelStyle}>
        <div style={dragHandleStyle} />

        {/* HEADER — recipe identity + servings stepper inline. */}
        <div style={headerSectionStyle}>
          <div style={kickerStyle}>
            {isEatAnchor
              ? <>EAT TIME{isRequest ? " · REQUESTED" : ""}</>
              : <>{meal.meal_slot ? meal.meal_slot.toUpperCase() : "SCHEDULED"}{isRequest ? " · REQUESTED" : ""}</>
            }
          </div>
          <div style={{ display: "flex", alignItems: "flex-start", gap: space.flow }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={titleStyle}>{recipe.title}</div>
              <div style={metaStyle}>
                {isGenPending
                  ? <>recipe being written now…</>
                  : isGenFailed
                    ? <>recipe generation didn't finish</>
                    : isConcept
                      ? <>~{totalTimeMin(recipe)} min · planned dish — recipe not written yet{recipe.cuisine ? ` · ${recipe.cuisine}` : ""}</>
                      : <>{totalTimeMin(recipe)} min · {difficultyLabel(recipe.difficulty)}{recipe.cuisine ? ` · ${recipe.cuisine}` : ""}</>}
              </div>
            </div>
            {showServings && (
              <ServingsStepper
                value={servings}
                onChange={(n) => onChangeServings(n)}
                label="SERVES"
                multipliers={[]}
                palette={{
                  surface: color.paper, btnBg: color.paper, btnBorder: color.hairline,
                  accent: color.warmBrown, accentSoft: "rgba(122,78,45,0.12)",
                  ink: color.ink, muted: color.inkFaint,
                  fontLabel: font.mono, fontNum: font.serif,
                }}
              />
            )}
          </div>

          {/* ANCHOR TOGGLE — reframes what the WHEN chips below mean.
              "Start cooking" treats the picked time as the cook
              start; "Want to eat" backdates the cook from this time
              by recipe duration. Same toggle shape SchedulePicker
              uses, so the mental model carries through. Hidden for
              non-creators / when no handler is wired. */}
          {canChangeAnchor && (
            <div style={{ marginTop: space.flow, display: "flex", alignItems: "center", gap: space.tight }}>
              <span style={fieldLabelStyle}>THIS IS WHEN I</span>
              <div style={{ display: "flex", gap: 4, flex: 1 }}>
                <button
                  onClick={() => !isEatAnchor || onChangeEatAnchor(false)}
                  style={anchorPillStyle(!isEatAnchor)}
                  disabled={!isEatAnchor}
                >
                  Start cooking
                </button>
                <button
                  onClick={() => isEatAnchor || onChangeEatAnchor(true)}
                  style={anchorPillStyle(isEatAnchor)}
                  disabled={isEatAnchor}
                >
                  Want to eat
                </button>
              </div>
            </div>
          )}

          {/* DATE + TIME — chips that act as native pickers. Creator
              can edit directly; others see static text in the same
              voice. Eat-anchored meals expose two rows: EAT (the
              picked time) + COOK starts (derived from duration). */}
          <div style={{ marginTop: space.flow, display: "flex", flexWrap: "wrap", gap: space.tight, alignItems: "center" }}>
            <span style={fieldLabelStyle}>{isEatAnchor ? "EAT" : "WHEN"}</span>
            {canReschedule ? (
              <>
                <DateChip value={toDateInput(sched)} onChange={applyDate} display={fmtFullDate(sched)} />
                <TimeChip value={toTimeInput(sched)} onChange={applyTime} display={fmtClock(sched)} />
              </>
            ) : (
              <>
                <span style={readOnlyChip}>{fmtFullDate(sched)}</span>
                <span style={readOnlyChip}>{fmtClock(sched)}</span>
              </>
            )}
          </div>
          {isEatAnchor && cookStart && (
            <div style={cookStartLineStyle}>
              Start cooking{" "}
              <b style={{ color: color.ink, fontWeight: 600 }}>
                {cookStart.toDateString() === eatTime.toDateString()
                  ? "today"
                  : SHORT_DAY[cookStart.getDay()]}
                {" "}
                {fmtClock(cookStart)}
              </b>
            </div>
          )}
          {/* Overnight bakes get a second window line — Day 2 starts at
              its own clock, distinct from "start cooking" (Day 1). The
              user thinks of these as two cooking sessions, so we name
              both. Eat-anchored: bake ends at eatTime; cook-anchored:
              bake ends after day2Min from the morning anchor. */}
          {overnightDur && day2StartTime && (
            <div style={cookStartLineStyle}>
              Bake Day 2{" "}
              <b style={{ color: color.ink, fontWeight: 600 }}>
                {SHORT_DAY[day2StartTime.getDay()]} {fmtClock(day2StartTime)}
              </b>
              {" → "}
              <b style={{ color: color.ink, fontWeight: 600 }}>
                {fmtClock(isEatAnchor
                  ? eatTime
                  : new Date(day2StartTime.getTime() + overnightDur.day2Min * 60_000))}
              </b>
            </div>
          )}
        </div>

        {/* SCROLLABLE BODY */}
        <div style={bodyScrollStyle}>
          {/* Attribution — only when there's something non-redundant. */}
          {(() => {
            const showCreator = !isCreator;
            const showCook    = cookName && cookName !== creatorName && !isCook;
            if (!showCreator && !showCook && !isRequest) return null;
            return (
              <div style={attributionStyle(isRequest)}>
                {isRequest
                  ? <><b style={{ color: color.burnt, fontWeight: 600 }}>{creatorName}</b> is hoping someone will cook this.</>
                  : <>
                      {showCreator && <>Scheduled by <b style={{ color: color.ink, fontWeight: 600 }}>{creatorName}</b></>}
                      {showCreator && showCook ? " · " : ""}
                      {showCook && <>Cooking: <b style={{ color: color.teal, fontWeight: 600 }}>{cookName}</b></>}
                    </>
                }
              </div>
            );
          })()}

          {!isCreator && meal.servings != null && (
            <div style={readoutBlockStyle}>
              Cooking for <b style={{ color: color.ink, fontWeight: 600 }}>{meal.servings}</b>
            </div>
          )}

          {isCreator && onChangeCook && family.length > 0 && (
            <div style={{ marginTop: space.gap }}>
              <div style={fieldLabelStyle}>REASSIGN</div>
              <div style={reassignRowStyle}>
                {[
                  { id: "request", label: "Request",  target: null },
                  { id: userId,    label: "Me",       target: userId },
                  ...family.filter(f => f.otherId && f.other?.name).map(f => ({
                    id: f.otherId,
                    label: f.other.name.trim().split(/\s+/)[0],
                    target: f.otherId,
                  })),
                ].map(opt => {
                  const isActive = (opt.target ?? null) === (meal.cook_id ?? null);
                  return (
                    <button
                      key={opt.id}
                      onClick={() => onChangeCook(opt.target)}
                      disabled={isActive}
                      style={chipBtn(isActive)}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {meal.note && (
            <div style={noteStyle}>"{meal.note}"</div>
          )}

          {prepRows.length > 0 ? (
            <MealPrepTimeline
              meal={meal}
              rows={prepRows}
              onDismiss={onDismissPrep}
              onUndismiss={onUndismissPrep}
            />
          ) : totalNotifs > 0 && (
            <div style={readoutBlockStyle}>
              {activeNotifs} of {totalNotifs} prep reminder{totalNotifs === 1 ? "" : "s"} on
            </div>
          )}

          <StepTimeline recipe={recipe} />
        </div>

        {/* FOOTER */}
        <div style={footerSectionStyle}>
          {!confirming && (meal.cook_id == null || isCook) && (
            meal.cook_id == null ? (
              <button onClick={onClaim} style={claimBtn}>I'll cook this</button>
            ) : (
              <button onClick={onUnclaim} style={ghostQuietBtn}>‹ Ask family instead</button>
            )
          )}
          <div style={{ display: "flex", gap: space.tight }}>
            {confirming ? (
              <>
                <button onClick={() => setConfirming(false)} style={{ ...ghostQuietBtn, flex: 1 }}>Keep it</button>
                <button onClick={() => onDelete(meal.id)} style={{ ...dangerBtn, flex: 1 }}>Yes, remove</button>
              </>
            ) : isGenPending ? (
              <div style={{
                flex: 1, textAlign: "center", padding: "12px 8px",
                fontFamily: font.sans, fontSize: 13, color: color.inkFaint,
                lineHeight: 1.5,
              }}>
                Chadwick is writing this recipe — it fills in right here
                (and in your book). Feel free to close this.
              </div>
            ) : isGenFailed ? (
              <>
                <button onClick={() => setConfirming(true)} style={removeBtn}>Remove</button>
                <button onClick={onRetryGenerate} style={cookNowBtn}>Retry recipe ›</button>
              </>
            ) : isConcept ? (
              <>
                <button onClick={() => setConfirming(true)} style={removeBtn}>Remove</button>
                <button
                  onClick={generating ? undefined : onGenerateRecipe}
                  style={{ ...cookNowBtn, opacity: generating ? 0.6 : 1 }}
                  disabled={generating}
                >
                  {generating ? "Starting…" : "Generate full recipe ›"}
                </button>
              </>
            ) : (
              <>
                <button onClick={() => setConfirming(true)} style={removeBtn}>Remove</button>
                <button onClick={onCookNow} style={cookNowBtn}>Cook now ›</button>
              </>
            )}
          </div>
          <button onClick={onClose} style={closeLinkStyle}>Close</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ── Date / time chip controls ─────────────────────────────────────
//
// Visual chip is the button the user sees; an invisible native
// `<input type="date|time">` lives underneath. Tapping the chip
// calls `inputRef.current.showPicker()` (modern browsers) or falls
// back to `.click()` so the OS-level picker opens reliably even when
// the drawer is portal-rendered inside a transformed ancestor —
// Safari's quirk where the label/hidden-input pattern silently
// drops the picker request.
function DateChip({ value, onChange, display }) {
  const ref = useRef(null);
  const open = () => {
    const el = ref.current;
    if (!el) return;
    if (typeof el.showPicker === "function") {
      try { el.showPicker(); return; } catch (_) { /* fall through */ }
    }
    el.focus();
    el.click();
  };
  return (
    <button type="button" onClick={open} style={chipFieldStyle} title="Change date">
      <span style={chipIconStyle} aria-hidden>‹›</span>
      <span style={chipTextStyle}>{display}</span>
      <input
        ref={ref}
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={hiddenInputStyle}
        tabIndex={-1}
        aria-hidden
      />
    </button>
  );
}
function TimeChip({ value, onChange, display }) {
  const ref = useRef(null);
  const open = () => {
    const el = ref.current;
    if (!el) return;
    if (typeof el.showPicker === "function") {
      try { el.showPicker(); return; } catch (_) { /* fall through */ }
    }
    el.focus();
    el.click();
  };
  return (
    <button type="button" onClick={open} style={chipFieldStyle} title="Change time">
      <span style={chipIconStyle} aria-hidden>‹›</span>
      <span style={chipTextStyle}>{display}</span>
      <input
        ref={ref}
        type="time"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={hiddenInputStyle}
        tabIndex={-1}
        aria-hidden
      />
    </button>
  );
}

// ── Date helpers ──────────────────────────────────────────────────

function fmtClock(d) {
  const dt = d instanceof Date ? d : new Date(d);
  const h = dt.getHours();
  const m = dt.getMinutes();
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
}
function fmtFullDate(d) {
  const dt = d instanceof Date ? d : new Date(d);
  return `${SHORT_DAY[dt.getDay()]}, ${MONTH_SHORT[dt.getMonth()]} ${dt.getDate()}`;
}
function toDateInput(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function toTimeInput(d) {
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

// ── styles ────────────────────────────────────────────────────────

const overlayStyle = {
  position: "fixed", inset: 0,
  background: "rgba(30,24,16,0.55)",
  backdropFilter: "blur(4px)",
  WebkitBackdropFilter: "blur(4px)",
  zIndex: 280,
  display: "flex", alignItems: "flex-end", justifyContent: "center",
  // Safe-area handling moved INSIDE the panel's footer (see
  // footerSectionStyle below). Was on the overlay as bottom padding,
  // which floated the entire panel ~34px above the home indicator
  // on notched iPhones and showed a visible gap of cream backdrop
  // below the Close link. Now: panel hugs the screen edge; footer
  // pads itself for the safe area so the Close link still clears
  // the home indicator.
  padding: 0,
};

const panelStyle = {
  width: "100%",
  maxWidth: 520,
  maxHeight: "92vh",
  background: color.paper,
  borderTopLeftRadius: radius.sheet,
  borderTopRightRadius: radius.sheet,
  boxShadow: shadow.lift,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const dragHandleStyle = {
  width: 40, height: 4,
  background: color.hairline,
  borderRadius: radius.pill,
  margin: `${space.inline}px auto ${space.tight}px`,
  flexShrink: 0,
};

const headerSectionStyle = {
  flexShrink: 0,
  padding: `${space.inline}px ${space.gap}px ${space.flow}px`,
  borderBottom: `1px solid ${color.hairline}`,
  background: color.paper,
};

const kickerStyle = {
  fontFamily: font.mono,
  fontSize: 10,
  color: color.mustard,
  letterSpacing: "0.14em",
  marginBottom: space.tight,
  fontWeight: 600,
};

const titleStyle = {
  fontFamily: font.itemTitle,
  fontStyle: "italic",
  fontWeight: 500,
  fontSize: 26,
  color: color.ink,
  lineHeight: 1.1,
  display: "-webkit-box",
  // Was 2 — clipped 3-word recipe titles like "Brioche Loaf with
  // Gjetost Confit Swirl" mid-word ("Confit..."). 3 lines fits
  // every recipe title we've generated and still leaves the meta
  // line + servings stepper readable in the header without
  // scrolling.
  WebkitLineClamp: 3,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  overflowWrap: "anywhere",
  letterSpacing: "-0.01em",
};

const metaStyle = {
  fontFamily: font.sans,
  fontSize: 12,
  color: color.inkFaint,
  marginTop: space.nudge,
  letterSpacing: "0.01em",
};

const inlineServingsStyle = {
  display: "flex", alignItems: "center", gap: space.tight,
  flexShrink: 0,
  background: color.glassFillHeavy,
  border: `1px solid ${color.hairline}`,
  borderRadius: radius.pill,
  padding: `${space.nudge}px ${space.tight}px`,
  boxShadow: shadow.soft,
};

const stepperBtn = {
  width: 28, height: 28,
  borderRadius: radius.pill,
  background: color.paper,
  border: `1px solid ${color.hairline}`,
  color: color.warmBrown,
  fontFamily: font.serif,
  fontSize: 16, lineHeight: 1, padding: 0,
  cursor: "pointer", flexShrink: 0,
};

const servingsCountStyle = {
  display: "flex", flexDirection: "column", alignItems: "center",
  minWidth: 36,
  gap: 1,
};

const fieldLabelStyle = {
  fontFamily: font.mono,
  fontSize: 9,
  color: color.inkFaint,
  letterSpacing: "0.14em",
  fontWeight: 600,
  marginRight: space.tight,
  marginBottom: space.tight,
};

const anchorPillStyle = (active) => ({
  flex: 1,
  padding: `${space.tight}px ${space.inline}px`,
  background: active ? color.mustardTint : color.glassFillHeavy,
  border: `1px solid ${active ? "rgba(212,166,55,0.55)" : color.hairline}`,
  color: active ? color.warmBrown : color.inkMuted,
  borderRadius: radius.pill,
  fontFamily: font.sans,
  fontWeight: 600,
  fontSize: 12,
  letterSpacing: "0.02em",
  cursor: active ? "default" : "pointer",
  whiteSpace: "nowrap",
});

const chipFieldStyle = {
  position: "relative",
  display: "inline-flex",
  alignItems: "center",
  gap: space.tight,
  padding: `${space.tight}px ${space.inline}px`,
  background: color.glassFillHeavy,
  border: `1px solid ${color.hairline}`,
  borderRadius: radius.pill,
  boxShadow: shadow.soft,
  cursor: "pointer",
  fontFamily: font.serif,
  fontStyle: "italic",
  fontWeight: 500,
  fontSize: 14,
  color: color.ink,
  whiteSpace: "nowrap",
};
const chipIconStyle = {
  fontFamily: font.mono,
  fontSize: 9,
  color: color.mustard,
  letterSpacing: "0.05em",
};
const chipTextStyle = {
  fontFamily: font.serif,
  fontStyle: "italic",
  fontWeight: 500,
  color: color.ink,
};
const hiddenInputStyle = {
  position: "absolute",
  inset: 0,
  width: "100%", height: "100%",
  opacity: 0,
  cursor: "pointer",
  border: 0, padding: 0,
};

const readOnlyChip = {
  display: "inline-flex",
  alignItems: "center",
  padding: `${space.tight}px ${space.inline}px`,
  background: color.glassFillHeavy,
  border: `1px solid ${color.hairline}`,
  borderRadius: radius.pill,
  fontFamily: font.serif,
  fontStyle: "italic",
  fontWeight: 500,
  fontSize: 14,
  color: color.inkMuted,
  whiteSpace: "nowrap",
};

const cookStartLineStyle = {
  fontFamily: font.sans,
  fontSize: 12,
  color: color.inkFaint,
  marginTop: space.tight,
  letterSpacing: "0.01em",
};

const bodyScrollStyle = {
  flex: 1,
  overflowY: "auto",
  WebkitOverflowScrolling: "touch",
  padding: `${space.flow}px ${space.gap}px ${space.gap}px`,
  background: color.paper,
};

const footerSectionStyle = {
  flexShrink: 0,
  // Bottom padding includes env(safe-area-inset-bottom) so the
  // Close link clears the iOS home indicator without leaving an
  // empty-cream gap at the bottom of the drawer (the gap was a
  // symptom of the safe-area inset living on the overlay instead
  // of the footer). Top + sides keep the standard rhythm.
  padding: `${space.flow}px ${space.gap}px calc(${space.gap}px + env(safe-area-inset-bottom))`,
  borderTop: `1px solid ${color.hairline}`,
  background: color.paper,
  ...stack(space.tight),
};

const attributionStyle = (isRequest) => ({
  padding: `${space.inline}px ${space.flow}px`,
  background: isRequest ? color.mustardTint : color.glassFillHeavy,
  border: `1px solid ${isRequest ? "rgba(212,166,55,0.42)" : color.hairline}`,
  borderRadius: radius.field,
  fontFamily: font.sans,
  fontSize: 13,
  color: color.inkMuted,
  lineHeight: 1.5,
});

const readoutBlockStyle = {
  marginTop: space.inline,
  padding: `${space.tight}px ${space.flow}px`,
  background: color.glassFillHeavy,
  border: `1px solid ${color.hairline}`,
  borderRadius: radius.field,
  fontFamily: font.sans,
  fontSize: 12,
  color: color.inkMuted,
};

const reassignRowStyle = {
  display: "flex",
  gap: space.tight,
  overflowX: "auto",
  paddingBottom: space.nudge,
  WebkitOverflowScrolling: "touch",
  scrollbarWidth: "none",
};

const chipBtn = (isActive) => ({
  flexShrink: 0,
  padding: `${space.tight}px ${space.flow}px`,
  background: isActive ? color.mustardTint : color.glassFillHeavy,
  border: `1px solid ${isActive ? color.mustard : color.hairline}`,
  color: isActive ? color.warmBrown : color.inkMuted,
  borderRadius: radius.pill,
  fontFamily: font.serif,
  fontStyle: "italic",
  fontWeight: 500,
  fontSize: 14,
  whiteSpace: "nowrap",
  cursor: isActive ? "default" : "pointer",
  boxShadow: isActive ? "none" : shadow.soft,
});

const noteStyle = {
  marginTop: space.inline,
  padding: `${space.inline}px ${space.flow}px`,
  background: color.brownTint,
  border: `1px solid rgba(122,78,45,0.22)`,
  borderRadius: radius.field,
  fontFamily: font.serif,
  fontSize: 14,
  color: color.warmBrown,
  fontStyle: "italic",
  lineHeight: 1.4,
};

const infoCardStyle = {
  padding: `${space.flow}px ${space.gap}px`,
  background: color.glassFillHeavy,
  border: `1px solid ${color.hairline}`,
  borderRadius: radius.card,
  fontFamily: font.sans,
  fontSize: 13,
  color: color.inkMuted,
  lineHeight: 1.5,
  boxShadow: shadow.soft,
};

// Footer buttons — Forma DJR Deck (font.sans), no italic. The
// editorial italic is reserved for content (title, dates, names);
// actions use the cleaner geometric sans so the bottom of the
// drawer reads as a modern action row, not a 1990s magazine pull-
// quote. Letter-spacing nudged to 0.04em for slight technical
// formality without going full uppercase-tracking.
const buttonBase = {
  fontFamily: font.sans,
  fontWeight: 600,
  fontSize: 14,
  letterSpacing: "0.02em",
  borderRadius: radius.pill,
  padding: `${space.flow}px ${space.gap}px`,
  cursor: "pointer",
  transition: "background 160ms ease, border-color 160ms ease",
};

const claimBtn = {
  ...buttonBase,
  width: "100%",
  background: color.tealTint,
  border: `1px solid ${color.aqua}`,
  color: color.teal,
};

const ghostQuietBtn = {
  ...buttonBase,
  width: "100%",
  background: color.glassFillHeavy,
  border: `1px solid ${color.hairline}`,
  color: color.inkMuted,
};

const removeBtn = {
  ...buttonBase,
  background: "transparent",
  border: `1px solid ${color.hairline}`,
  color: color.inkFaint,
};

const cookNowBtn = {
  ...buttonBase,
  flex: 1,
  background: "linear-gradient(180deg, #C85A1F 0%, #A34711 100%)",
  border: "1px solid rgba(255,255,255,0.35)",
  color: "#FFF8EE",
  fontSize: 15,
  letterSpacing: "0.04em",
  boxShadow: shadow.cta,
};

const dangerBtn = {
  ...buttonBase,
  width: "100%",
  background: "rgba(217,107,43,0.10)",
  border: "1px solid rgba(217,107,43,0.45)",
  color: color.burnt,
};

const closeLinkStyle = {
  width: "100%",
  background: "none", border: "none",
  color: color.inkFaint,
  fontFamily: font.sans,
  fontSize: 12,
  fontWeight: 500,
  letterSpacing: "0.04em",
  cursor: "pointer",
  padding: space.tight,
};
